/**
 * Markdown pipeline configuration shared by every {@link Response}.
 *
 * Both exports must stay module-level constants. Streamdown's inner `Block`
 * memo compares `components` key-by-key and `rehypePlugins` by reference, so an
 * inline object literal would re-render every block on every streamed token.
 */

import type { Pluggable } from 'unified';
import type { MermaidOptions } from 'streamdown';
import { defaultRehypePlugins, defaultRemarkPlugins } from 'streamdown';
import { CitationAnchor } from '@cotera/client/v0/chat/citations/citation-chip';
import { rehypeGroupCitations } from '@cotera/client/v0/chat/citations/rehype-group-citations';
import { remarkSanitizeMermaid } from '@cotera/client/v0/chat/mermaid/remark-sanitize-mermaid';
import { MermaidErrorCard } from '@cotera/client/v0/chat/mermaid/mermaid-error-card';
import { MERMAID_THEME_CONFIG } from '@cotera/client/v0/chat/mermaid/mermaid-theme';
import { ResponsePre } from '@cotera/client/v0/chat/mermaid/mermaid-collapsible';
import { ResponseTable } from './response-table';

/**
 * Rebuild one of Streamdown's default `[plugin, options]` entries with extra
 * options merged in. Non-tuple entries are returned untouched.
 */
function withOptions(
  entry: Pluggable,
  extraOptions: Record<string, unknown>
): Pluggable {
  if (!Array.isArray(entry)) {
    return entry;
  }
  const [plugin, options] = entry;
  const merged =
    typeof options === 'object' && options !== null
      ? { ...options, ...extraOptions }
      : extraOptions;
  return [plugin, merged];
}

/**
 * Streamdown's defaults, with two changes.
 *
 * **`linkBlockPolicy: 'remove'`.** While a link is still streaming, Streamdown
 * rewrites it to `[label](streamdown:incomplete-link)`; `rehype-sanitize`
 * strips that href because the GitHub schema only permits
 * http/https/irc/ircs/mailto/xmpp; `rehype-harden` then sees an hrefless anchor
 * and — under its own default policy of `'indicator'`, which Streamdown never
 * overrides — replaces it with a grey `label [blocked]` span. That flickers on
 * every link Coco writes. `'remove'` drops the in-flight anchor instead, so the
 * finished link (or chip) simply appears.
 *
 * **`rehypeGroupCitations` runs last.** It has to: sanitize's allow-list for
 * `<a>` would strip the grouping property if it were written any earlier.
 */
export const RESPONSE_REHYPE_PLUGINS: Pluggable[] = [
  // Object insertion order is the pipeline order: raw, katex, sanitize, harden.
  ...Object.entries(defaultRehypePlugins).map(([name, entry]) =>
    name === 'harden'
      ? withOptions(entry, { linkBlockPolicy: 'remove' })
      : entry
  ),
  rehypeGroupCitations,
];

/**
 * Streamdown's default remark plugins plus {@link remarkSanitizeMermaid}.
 * Passing `remarkPlugins` to Streamdown *replaces* its defaults rather than
 * merging, so the defaults are spread back in first — omit them and
 * tables/math stop rendering. Kept module-level for the same reference-equality
 * reason as the rehype list above.
 *
 * `defaultRemarkPlugins` is typed `Record<string, Pluggable>` (currently the
 * ordered keys `gfm, math, cjkFriendly, cjkFriendlyGfmStrikethrough`), so
 * `Object.values` is the explicit object→array conversion. Insertion order is
 * preserved by JS for string keys, matching how {@link RESPONSE_REHYPE_PLUGINS}
 * treats `defaultRehypePlugins`. Our plugin is appended last; it only rewrites
 * `mermaid` code nodes, so relative ordering against the defaults is
 * immaterial.
 */
export const RESPONSE_REMARK_PLUGINS: Pluggable[] = [
  ...Object.values(defaultRemarkPlugins),
  remarkSanitizeMermaid,
];

/**
 * Streamdown's two Mermaid slots, both replaced.
 *
 * `config` swaps mermaid's stock lavender-on-white `default` theme for
 * {@link MERMAID_THEME_CONFIG}, the app's own palette.
 *
 * `errorComponent` replaces Streamdown's default error surface — the raw parser
 * dump — with {@link MermaidErrorCard}, a calm card that hides the code/error
 * behind disclosures.
 *
 * Module-level for the same reference-equality reason as the plugin lists
 * above: Streamdown puts this object on context, so a fresh literal would
 * re-render every block on every streamed token.
 */
export const RESPONSE_MERMAID_OPTIONS: MermaidOptions = {
  config: MERMAID_THEME_CONFIG,
  errorComponent: MermaidErrorCard,
};

/**
 * Streamdown merges these over its own component map (`{ ...defaults, ...ours }`),
 * so only the three tags below change.
 *
 * `a` is the override citations need — grouping happens in the rehype plugin, so
 * no container component (`p`, `li`, `td`, …) has to be reimplemented.
 * `CitationAnchor` renders a plain anchor when there is no citation registry or
 * the link isn't a citation.
 *
 * `table` swaps the plain HTML table for the shared read-only data-grid; it
 * reads each cell's already-rendered rich content (including citation chips) off
 * the children Streamdown hands it, so the `a` override still applies inside.
 *
 * `pre` clips over-tall Mermaid diagrams behind an expand control. Streamdown's
 * own `pre` is a passthrough and its `code` component builds the whole mermaid
 * block, which makes `pre` the one seam that can wrap a finished diagram without
 * reimplementing it — see {@link ResponsePre}.
 */
export const RESPONSE_MARKDOWN_COMPONENTS = {
  a: CitationAnchor,
  table: ResponseTable,
  pre: ResponsePre,
};
